/**
 * Transaction Service
 *
 * CRUD operations for transactions.
 * All operations are local-first (IndexedDB).
 */

import { db, Transaction, SyncStatus } from './db';
import { generateId } from '../utils/id';
import { parseSearchQuery, matchesSearchQuery } from './searchQuery';
import { syncManager } from './syncManager';
import { accountService } from './accounts';
import { dataEvents } from './dataEvents';
import { scheduledTransactionService } from './scheduledTransactions';

// ============================================================
// Types
// ============================================================

export type TransactionInput = Omit<
  Transaction,
  'id' | 'createdAt' | 'updatedAt' | 'syncStatus'
>;

export interface TransactionFilter {
  accountId?: string;
  type?: Transaction['type'];
  categoryId?: string;
  startDate?: Date;
  endDate?: Date;
  search?: string;
  amountMin?: number;
  amountMax?: number;
  limit?: number;
  offset?: number;
}

// ============================================================
// Debug Logging
// ============================================================

const DEBUG =
  import.meta.env.VITE_DEBUG === 'true' ||
  (typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).has('debug'));

function log(...args: unknown[]): void {
  if (DEBUG) {
    console.log('[TXN]', ...args);
  }
}

// ============================================================
// CRUD Operations
// ============================================================

export const transactionService = {
  /**
   * Create a new transaction.
   * Also updates account balance(s) incrementally.
   */
  async create(data: TransactionInput): Promise<string> {
    // BUG-003 FIX: Validate transfers have destination account
    if (data.type === 'transfer' && !data.toAccountId) {
      throw new Error('Transfer must have destination account (toAccountId)');
    }

    // BUG-014 FIX: Warn on cross-currency transfers (no FX conversion implemented)
    if (data.type === 'transfer' && data.toAccountId) {
      const fromAccount = await accountService.getById(data.accountId);
      const toAccount = await accountService.getById(data.toAccountId);
      if (fromAccount && toAccount && fromAccount.currency !== toAccount.currency) {
        console.warn(
          '[TXN] Cross-currency transfer:',
          fromAccount.currency, '->', toAccount.currency,
          '| Amount:', data.amount,
          '| No FX conversion applied'
        );
      }
    }

    const id = generateId();
    const now = new Date();

    // Ensure date is a valid Date object
    // This prevents transactions from being "invisible" in queries
    let txDate = data.date;
    if (!(txDate instanceof Date) || isNaN(txDate.getTime())) {
      // If date is invalid, use current date/time as fallback
      log('[WARN] Invalid date provided, using current date');
      txDate = now;
    }

    const transaction: Transaction = {
      ...data,
      id,
      date: txDate,
      createdAt: now,
      updatedAt: now,
      syncStatus: 'pending',
    };

    // ATOMIC: Insert transaction + update balance in single DB transaction
    // Prevents race conditions when multiple transactions created simultaneously
    await db.transaction('rw', [db.transactions, db.accounts], async () => {
      await db.transactions.add(transaction);
      await this.adjustAccountBalances(data.type, data.amount, data.accountId, data.toAccountId);
    });
    log('Created transaction:', id);

    // Trigger debounced sync
    syncManager.triggerPush();

    // Notify UI to refresh
    dataEvents.emit('transactions');
    dataEvents.emit('accounts'); // Balance changed too

    return id;
  },

  /**
   * Get a transaction by ID.
   */
  async getById(id: string): Promise<Transaction | undefined> {
    return db.transactions.get(id);
  },

  /**
   * Get all transactions for an account.
   * Excludes soft-deleted transactions.
   */
  async getByAccount(accountId: string): Promise<Transaction[]> {
    const all = await db.transactions
      .where('accountId')
      .equals(accountId)
      .reverse()
      .sortBy('date');
    return all.filter((t) => !t.deletedAt);
  },

  /**
   * Get transactions by date range.
   * Excludes soft-deleted transactions.
   *
   * NOTE: Uses .toArray() + JS filter instead of .where('date').between()
   * because IndexedDB silently skips records with invalid Date values.
   * See DATABASE_PLAN.md "Dexie orderBy Bug" section.
   */
  async getByDateRange(start: Date, end: Date): Promise<Transaction[]> {
    // Use toArray + filter for robustness against invalid dates in legacy data
    // The indexed query (.where.between) would silently skip Invalid Date records
    const all = await db.transactions.toArray();
    return all
      .filter((t) => !t.deletedAt)
      .filter((t) => {
        const d = t.date;
        if (!(d instanceof Date) || isNaN(d.getTime())) return false;
        return d >= start && d <= end;
      })
      .sort((a, b) => b.date.getTime() - a.date.getTime());
  },

  /**
   * Get recent transactions.
   * Excludes soft-deleted transactions.
   *
   * PERFORMANCE: Uses DB-level limit - only fetches what's needed.
   */
  async getRecent(limit: number = 50): Promise<Transaction[]> {
    // Fetch 2x limit to account for soft-deleted records being filtered out
    const fetchLimit = limit * 2;

    const results = await db.transactions
      .orderBy('date')
      .reverse()
      .limit(fetchLimit)
      .toArray();

    // Filter and slice (now operating on small array, not 1700+ records)
    return results.filter((t) => !t.deletedAt).slice(0, limit);
  },

  /**
   * Get transactions with filters.
   * Excludes soft-deleted transactions.
   *
   * PERFORMANCE: Uses DB-level pagination. Only fetches ~100 records
   * instead of 1700+. The indexed query is O(1) for offset/limit.
   */
  async getFiltered(filter: TransactionFilter): Promise<Transaction[]> {
    const limit = filter.limit || 50;
    const offset = filter.offset || 0;

    // Determine fetch strategy based on filters
    const hasComplexFilters = !!(filter.accountId || filter.type || filter.categoryId ||
                                  filter.startDate || filter.endDate || filter.search ||
                                  filter.amountMin !== undefined || filter.amountMax !== undefined);

    let results: Transaction[];

    if (hasComplexFilters) {
      // Complex filters: need to fetch records and filter client-side
      //
      // PERFORMANCE OPTIMIZATION:
      // - Use IndexedDB .where('date') for date-bounded queries (fast, uses index)
      // - Only fetch all records for "All Time" searches
      // - This keeps "This Month" and other date presets snappy

      if (filter.startDate && filter.endDate) {
        // Both dates - use indexed range query (FAST)
        log('[TXN] Date range query:', filter.startDate.toDateString(), '-', filter.endDate.toDateString());
        const all = await db.transactions
          .where('date')
          .between(filter.startDate, filter.endDate, true, true)
          .reverse()
          .toArray();
        results = all.filter((t) => !t.deletedAt);
      } else if (filter.startDate) {
        // Only start date - fetch from that date forward (FAST)
        log('[TXN] From date query:', filter.startDate.toDateString());
        const all = await db.transactions
          .where('date')
          .aboveOrEqual(filter.startDate)
          .reverse()
          .toArray();
        results = all.filter((t) => !t.deletedAt);
      } else {
        // No date filter (All Time) - full table scan (slower but rare)
        log('[TXN] All Time - full scan');
        const all = await db.transactions.orderBy('date').reverse().toArray();
        results = all.filter((t) => !t.deletedAt);
      }

      // Apply remaining filters (already filtered by date above)
      if (filter.accountId) {
        results = results.filter((t) => t.accountId === filter.accountId);
      }
      if (filter.type) {
        results = results.filter((t) => t.type === filter.type);
      }
      if (filter.categoryId) {
        results = results.filter((t) => t.categoryId === filter.categoryId);
      }
      if (filter.search) {
        // NFKC-normalized search (F5 Phase 1) with multi-term support:
        // whitespace = AND, comma = OR (see services/searchQuery.ts).
        const parsed = parseSearchQuery(filter.search);
        results = results.filter((t) => matchesSearchQuery(parsed, t.description, t.tags));
      }
      if (filter.amountMin !== undefined && Number.isFinite(filter.amountMin)) {
        results = results.filter((t) => t.amount >= filter.amountMin!);
      }
      if (filter.amountMax !== undefined && Number.isFinite(filter.amountMax)) {
        results = results.filter((t) => t.amount <= filter.amountMax!);
      }

      // Apply pagination
      return results.slice(offset, offset + limit);

    } else {
      // No complex filters - use efficient DB-level pagination
      // Fetch 3x limit to account for soft-deleted records
      const fetchLimit = (offset + limit) * 3;

      results = await db.transactions
        .orderBy('date')
        .reverse()
        .limit(fetchLimit)
        .toArray();

      // Filter soft-deleted and apply pagination
      const filtered = results.filter((t) => !t.deletedAt);
      return filtered.slice(offset, offset + limit);
    }
  },

  /**
   * Update a transaction.
   * Also adjusts account balance(s) if amount, type, or account changes.
   */
  async update(
    id: string,
    data: Partial<Omit<Transaction, 'id' | 'createdAt'>>
  ): Promise<void> {
    // Get old transaction to calculate balance delta
    const oldTx = await db.transactions.get(id);

    // BUG-019 FIX: Prevent changing toAccountId on transfers
    // Changing the destination would leave the old destination's balance corrupted
    // User should delete and recreate the transfer instead
    if (oldTx && oldTx.type === 'transfer' && data.toAccountId !== undefined && data.toAccountId !== oldTx.toAccountId) {
      throw new Error('Cannot change transfer destination. Delete and recreate the transfer instead.');
    }

    // Validate date if being updated (prevent invalid dates from breaking queries)
    let updateData = { ...data };
    if (data.date !== undefined) {
      const txDate = data.date;
      if (!(txDate instanceof Date) || isNaN(txDate.getTime())) {
        log('[WARN] Invalid date in update, using current date');
        updateData.date = new Date();
      }
    }

    // ATOMIC: Update transaction + rebalance in single DB transaction
    // Prevents race conditions with concurrent updates
    await db.transaction('rw', [db.transactions, db.accounts], async () => {
      await db.transactions.update(id, {
        ...updateData,
        updatedAt: new Date(),
        syncStatus: 'pending',
      });

      // Adjust balances if transaction values changed
      if (oldTx) {
        const hasBalanceImpact =
          data.amount !== undefined ||
          data.type !== undefined ||
          data.accountId !== undefined ||
          data.toAccountId !== undefined;

        if (hasBalanceImpact) {
          // Reverse old transaction's effect
          await this.adjustAccountBalances(
            oldTx.type,
            -oldTx.amount,
            oldTx.accountId,
            oldTx.toAccountId
          );

          // Apply new transaction's effect
          const newType = data.type ?? oldTx.type;
          const newAmount = data.amount ?? oldTx.amount;
          const newAccountId = data.accountId ?? oldTx.accountId;
          const newToAccountId = data.toAccountId ?? oldTx.toAccountId;

          await this.adjustAccountBalances(newType, newAmount, newAccountId, newToAccountId);
        }
      }
    });
    log('Updated transaction:', id);

    // BUG-039/BUG-040 FIX: Update parent scheduled transaction when date changed
    // This updates both dayOfMonth AND nextDueDate so future recurrences happen on the new day
    if (oldTx?.scheduledTransactionId && data.date !== undefined) {
      const newDate = updateData.date as Date;
      const oldDate = oldTx.date;

      // Only update schedule if date actually changed
      if (newDate.getTime() !== oldDate.getTime()) {
        try {
          const schedule = await scheduledTransactionService.getById(
            oldTx.scheduledTransactionId
          );

          if (schedule && schedule.frequency === 'monthly') {
            const newDayOfMonth = newDate.getDate();

            // Calculate new nextDueDate: 1 month after the edited transaction date
            const newNextDueDate = new Date(newDate);
            newNextDueDate.setMonth(newNextDueDate.getMonth() + 1);
            newNextDueDate.setDate(newDayOfMonth); // Ensure correct day

            // Update parent schedule's dayOfMonth AND nextDueDate
            await scheduledTransactionService.update(oldTx.scheduledTransactionId, {
              dayOfMonth: newDayOfMonth,
              nextDueDate: newNextDueDate,
            });

            log('[OK] Updated parent schedule: dayOfMonth:', newDayOfMonth, 'nextDueDate:', newNextDueDate);
          }
        } catch (err) {
          // Don't fail transaction update if schedule update fails
          console.warn('[WARN] Failed to update parent schedule:', err);
        }
      }
    }

    // BUG-071 FIX: Update parent scheduled transaction when category changed
    // This ensures future generated transactions use the new category
    if (oldTx?.scheduledTransactionId && data.categoryId !== undefined && data.categoryId !== oldTx.categoryId) {
      try {
        await scheduledTransactionService.update(oldTx.scheduledTransactionId, {
          categoryId: data.categoryId,
        });
        log('[OK] Updated parent schedule categoryId:', data.categoryId);
      } catch (err) {
        // Don't fail transaction update if schedule update fails
        console.warn('[WARN] Failed to update parent schedule category:', err);
      }
    }

    // Trigger debounced sync
    syncManager.triggerPush();

    // Notify UI to refresh
    dataEvents.emit('transactions');
    dataEvents.emit('accounts'); // Balance may have changed
  },

  /**
   * Delete a transaction (soft delete for sync).
   * Marks as deleted instead of removing, so sync can propagate to other devices.
   * Also reverses the balance adjustment on affected accounts.
   */
  async delete(id: string): Promise<void> {
    // Get the transaction first to reverse its balance impact
    const transaction = await db.transactions.get(id);
    if (transaction) {
      // ATOMIC: Soft delete + reverse balance in single DB transaction
      // Prevents race conditions with concurrent deletes
      await db.transaction('rw', [db.transactions, db.accounts], async () => {
        // Reverse the balance adjustment (negate the original effect)
        await this.adjustAccountBalances(
          transaction.type,
          -transaction.amount, // Negate to reverse
          transaction.accountId,
          transaction.toAccountId
        );

        // Soft delete: mark as deleted instead of removing
        // This allows sync to propagate the delete to other devices
        await db.transactions.update(id, {
          deletedAt: new Date(),
          syncStatus: 'pending',
          updatedAt: new Date(),
        });
      });
      log('Soft deleted transaction:', id);
    }

    // Trigger debounced sync
    syncManager.triggerPush();

    // Notify UI to refresh
    dataEvents.emit('transactions');
    dataEvents.emit('accounts'); // Balance changed
  },

  /**
   * Hard delete a transaction (for cleanup after sync).
   * Only used internally after sync confirms delete propagated.
   */
  async hardDelete(id: string): Promise<void> {
    await db.transactions.delete(id);
    log('Hard deleted transaction:', id);
  },

  /**
   * Bulk create transactions (for import).
   * Updates account balances atomically with the inserts.
   */
  async bulkCreate(transactions: TransactionInput[]): Promise<string[]> {
    const now = new Date();
    const withIds: Transaction[] = transactions.map((t) => {
      // Validate date for each transaction
      let txDate = t.date;
      if (!(txDate instanceof Date) || isNaN(txDate.getTime())) {
        txDate = now;
      }
      return {
        ...t,
        date: txDate,
        id: generateId(),
        createdAt: now,
        updatedAt: now,
        syncStatus: 'pending' as SyncStatus,
      };
    });

    // ATOMIC: Insert all transactions + update all account balances
    await db.transaction('rw', [db.transactions, db.accounts], async () => {
      await db.transactions.bulkAdd(withIds);

      // Calculate balance deltas per account (efficient batch approach)
      const accountDeltas = new Map<string, number>();

      for (const t of withIds) {
        // Source account delta
        let delta = 0;
        if (t.type === 'income') {
          delta = t.amount;
        } else if (t.type === 'expense') {
          delta = -t.amount;
        } else if (t.type === 'transfer') {
          delta = -t.amount;
          // Destination account gets positive delta
          if (t.toAccountId) {
            const toDelta = accountDeltas.get(t.toAccountId) || 0;
            accountDeltas.set(t.toAccountId, toDelta + t.amount);
          }
        }
        const currentDelta = accountDeltas.get(t.accountId) || 0;
        accountDeltas.set(t.accountId, currentDelta + delta);
      }

      // Apply all deltas atomically
      for (const [accountId, delta] of accountDeltas.entries()) {
        const account = await db.accounts.get(accountId);
        if (account) {
          await db.accounts.update(accountId, {
            balance: account.balance + delta,
            updatedAt: now,
            syncStatus: 'pending',
          });
        }
      }
    });

    log('Bulk created', withIds.length, 'transactions with balance updates');

    // Trigger debounced sync (will batch with other changes)
    syncManager.triggerPush();

    // Notify UI to refresh
    dataEvents.emit('transactions');
    dataEvents.emit('accounts');

    return withIds.map((t) => t.id!);
  },

  /**
   * Get pending sync transactions.
   */
  async getPendingSync(): Promise<Transaction[]> {
    return db.transactions.where('syncStatus').equals('pending').toArray();
  },

  /**
   * Mark transactions as synced.
   */
  async markSynced(ids: string[]): Promise<void> {
    await db.transactions
      .where('id')
      .anyOf(ids)
      .modify({ syncStatus: 'synced' });
    log('Marked', ids.length, 'transactions as synced');
  },

  /**
   * Get transaction count (excludes soft-deleted).
   */
  async count(): Promise<number> {
    const all = await db.transactions.toArray();
    return all.filter((t) => !t.deletedAt).length;
  },

  /**
   * Get transaction totals by type for a date range.
   */
  async getTotals(
    startDate: Date,
    endDate: Date
  ): Promise<{ income: number; expense: number; transfer: number }> {
    const transactions = await this.getByDateRange(startDate, endDate);

    // Helper to check if transaction is a balance adjustment (not real income/expense)
    const isAdjustment = (desc: string | undefined): boolean => {
      if (!desc) return false;
      const lower = desc.toLowerCase();
      return lower.includes('[adjustment]') || lower.startsWith('adjustment');
    };

    return transactions.reduce(
      (totals, t) => {
        // Skip adjustments - they're balance corrections, not real income/expense
        if (!isAdjustment(t.description)) {
          totals[t.type] += t.amount;
        }
        return totals;
      },
      { income: 0, expense: 0, transfer: 0 }
    );
  },

  /**
   * Get spending by category for a date range.
   */
  async getByCategory(
    startDate: Date,
    endDate: Date
  ): Promise<Map<string, number>> {
    const transactions = await this.getByDateRange(startDate, endDate);
    const byCategory = new Map<string, number>();

    for (const t of transactions) {
      if (t.type === 'expense' && t.categoryId) {
        const current = byCategory.get(t.categoryId) || 0;
        byCategory.set(t.categoryId, current + t.amount);
      }
    }

    return byCategory;
  },

  /**
   * Adjust account balance(s) for a transaction within a Dexie transaction.
   *
   * IMPORTANT: Uses direct db.accounts calls instead of accountService.update()
   * to avoid triggering syncManager/dataEvents which would cause PrematureCommitError.
   * The caller is responsible for sync/events after the transaction completes.
   *
   * Balance logic:
   * - Income: account.balance += amount
   * - Expense: account.balance -= amount
   * - Transfer: fromAccount.balance -= amount, toAccount.balance += amount
   */
  async adjustAccountBalances(
    type: Transaction['type'],
    amount: number,
    accountId: string,
    toAccountId?: string
  ): Promise<void> {
    // Direct db access to stay within Dexie transaction scope
    const account = await db.accounts.get(accountId);
    if (!account) {
      log('[WARN] Account not found for balance update:', accountId);
      return;
    }

    let newBalance = account.balance;

    if (type === 'income') {
      newBalance += amount;
    } else if (type === 'expense') {
      newBalance -= amount;
    } else if (type === 'transfer') {
      newBalance -= amount; // Outgoing from source account
    }

    // Direct update - no side effects (sync/events handled by caller)
    await db.accounts.update(accountId, {
      balance: newBalance,
      updatedAt: new Date(),
      syncStatus: 'pending',
    });
    log('Updated balance:', account.name, account.balance, '->', newBalance);

    // Handle transfer destination
    // BUG-004 FIX: Throw error if destination account not found (was silent failure)
    if (type === 'transfer' && toAccountId) {
      const toAccount = await db.accounts.get(toAccountId);
      if (!toAccount) {
        console.error('[BALANCE] Transfer destination account not found:', toAccountId);
        throw new Error(`Destination account ${toAccountId} not found`);
      }
      const newToBalance = toAccount.balance + amount; // Incoming to destination
      // Direct update - no side effects
      await db.accounts.update(toAccountId, {
        balance: newToBalance,
        updatedAt: new Date(),
        syncStatus: 'pending',
      });
      log('Updated transfer dest:', toAccount.name, toAccount.balance, '->', newToBalance);
    }
  },
};
